import { Cabinet } from '../entities/Cabinet';
import { CabinetPieces } from '../Data';
import { CustomPiece, KewloxPieceType } from '../pieces/CustomPiece';
import { DoorHandle } from '../pieces/DoorHandle';
import { DrawerSlat } from '../pieces/DrawerSlat';
import { SimpleComponent } from '../pieces/SimpleComponent';
import { getPiecesData } from './dataStorage';
import { METAL_SUPPORT_IS_REFERENCE, Position } from '../constants';
import { Corner } from '../pieces/Corner';
import { Color } from '../entities/Color';

export class PiecesListItem<T extends KewloxPieceType> {
  piece: CustomPiece<T> | T;
  quantity: number;

  constructor(piece: CustomPiece<T> | T, quantity: number) {
    this.piece = piece;
    this.quantity = quantity;
  }

  get reference() {
    return this.piece.reference;
  }

  description() {
    return `${this.quantity} x ${this.piece.reference}`;
  }

  getTotalPrice() {
    return (this.quantity * this.piece.price).toFixed(2);
  }

  get totalPriceTaxIncl(): string {
    if (this.piece instanceof CustomPiece) {
      return (this.quantity * this.piece.priceTaxIncl).toFixed(2);
    } else {
      return (this.quantity * this.piece.price * 1.21).toFixed(2);
    }
  }

  get totalWeight() {
    return (this.quantity * this.piece.weight).toFixed(3);
  }

  isCustom(): boolean {
    if (
      this.piece instanceof CustomPiece &&
      !(this.piece.base instanceof Corner)
    ) {
      return this.piece.custom;
    }
    return false;
  }

  get color(): Color | undefined {
    if (this.piece instanceof CustomPiece) {
      // @ts-ignore
      if (this.piece.base && this.piece.base.color) {
        return this.piece.color;
      }
    } else {
      // @ts-ignore
      if (this.piece && this.piece.color) {
        // @ts-ignore
        return this.piece.color;
      }
    }
    return undefined;
  }

  get colorHexa(): string | undefined {
    if (this.color) return this.color.hexaHtml;

    return undefined;
  }

  get colorName(): string | undefined {
    if (this.color) return this.color.name;

    return undefined;
  }
}

export function aggregatePieces<T extends KewloxPieceType>(
  piece: CustomPiece<T> | T,
  piecesCollection: PiecesListItem<T>[],
) {
  let quantity = 1;
  if (piece instanceof CustomPiece) quantity = piece.quantity;

  for (const item of piecesCollection) {
    if (piece instanceof CustomPiece && piece.base instanceof SimpleComponent) {
      if (piece.reference === item.reference) {
        item.quantity += quantity;
        return;
      }
    } else if (item.piece.reference === piece.reference) {
      item.quantity += quantity;
      return;
    }
  }
  piecesCollection.push(new PiecesListItem<T>(piece, quantity));
}

type PieceType = keyof CabinetPieces;

export function arrayPieces(
  piecesList: CabinetPieces,
): {
  [id: string]: number;
} {
  let pieces = [];
  for (const element in piecesList) {
    pieces.push(piecesList[element as PieceType]);
  }

  pieces = pieces.flat();

  const result: { [id: string]: number } = {};
  for (const item of pieces) {
    if (item.reference in result) result[item.reference] += item.quantity;
    else result[item.reference] = item.quantity;
  }

  return result;
}

export function createCabinetPiecesList(cabinet: Cabinet): CabinetPieces {
  const piecesList: CabinetPieces = {
    battens: [],
    corners: [],
    crossPieces: [],
    panes: [],
    wheelsBases: [],
    doors: [],
    doorsHandles: [],
    plates: [],
    drawers: [],
    drawersHandles: [],
    drawersSlats: [],
    upperShelves: [],
    intermediateShelves: [],
    supportIntermediateShelves: [],
    reinforcementIntermediatesShelves: [],
    rods: [],
    others: [],
  };

  const {
    feets,
    wheels,
    wheelPane,
    shelves,
    corners,
    feetExtensions,
  } = cabinet;

  if (feets) {
    for (const feet of feets) {
      aggregatePieces(feet, piecesList.battens);
    }
  }

  if (wheels) {
    for (const wheel of wheels) {
      aggregatePieces(wheel, piecesList.others);
    }
  }

  if (wheelPane) {
    aggregatePieces(wheelPane, piecesList.wheelsBases);
  }

  for (const corner of corners) {
    aggregatePieces(corner, piecesList.corners);
  }

  if (feetExtensions) {
    for (const otherObject of feetExtensions) {
      aggregatePieces(otherObject, piecesList.others);
    }
  }

  for (const shelf of shelves) {
    for (const door of shelf.doors) {
      aggregatePieces(door, piecesList.doors);
      if (door.handle)
        aggregatePieces(door.handle as DoorHandle, piecesList.doorsHandles);
    }

    if (shelf.rod) {
      aggregatePieces(shelf.rod, piecesList.rods);
    }

    for (const drawer of shelf.drawers) {
      aggregatePieces(drawer, piecesList.drawers);
      if (drawer.handles) {
        for (const drawerHandle of drawer.handles) {
          aggregatePieces(drawerHandle, piecesList.drawersHandles);
        }
      } else if (drawer.handle) {
        aggregatePieces(
          drawer.handle as CustomPiece<DrawerSlat>,
          piecesList.drawersSlats,
        );
      }
    }

    for (const completeIntermediateShelf of shelf.intermediatesShelves) {
      aggregatePieces(
        completeIntermediateShelf.interShelf,
        piecesList.intermediateShelves,
      );
      for (const supportIS of completeIntermediateShelf.supports) {
        aggregatePieces(supportIS, piecesList.supportIntermediateShelves);
      }
      if (completeIntermediateShelf.reinforcement) {
        aggregatePieces(
          completeIntermediateShelf.reinforcement,
          piecesList.reinforcementIntermediatesShelves,
        );
      }
      if (completeIntermediateShelf.metalSupport > 0) {
        const metalSupportPiece = getPiecesData().others.getFromReference(
          METAL_SUPPORT_IS_REFERENCE,
        );
        if (!metalSupportPiece)
          throw new Error('Metal support (FR) not in data');
        aggregatePieces(
          new CustomPiece<SimpleComponent>(
            metalSupportPiece,
            false,
            Position.Front,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            completeIntermediateShelf.metalSupport,
          ),
          piecesList.intermediateShelves,
        );
      }
    }

    for (const rack of shelf.racks) {
      for (const pane of rack.panes) {
        aggregatePieces(pane, piecesList.panes);
      }

      for (const crossPiece of rack.crossPieces) {
        aggregatePieces(crossPiece, piecesList.crossPieces);
      }

      for (const batten of rack.battens) {
        aggregatePieces(batten, piecesList.battens);
      }

      if (rack.plate) {
        aggregatePieces(rack.plate, piecesList.plates);
      }
      if (rack.glassPlate) {
        aggregatePieces(rack.glassPlate, piecesList.plates);
      }

      if (rack.others) {
        for (const element of rack.others) {
          aggregatePieces(element, piecesList.others);
        }
      }
    }
  }

  if (cabinet.top.plate) {
    aggregatePieces(cabinet.top.plate, piecesList.plates);
    if (cabinet.top.glassPlate) {
      aggregatePieces(cabinet.top.glassPlate, piecesList.plates);
    }
  }

  if (cabinet.upperShelf) {
    aggregatePieces(cabinet.upperShelf, piecesList.others);
  }

  if (cabinet.top.others) {
    for (const element of cabinet.top.others) {
      aggregatePieces(element, piecesList.others);
    }
  }

  for (const crossPiece of cabinet.top.crossPieces) {
    aggregatePieces(crossPiece, piecesList.crossPieces);
  }

  return piecesList;
}
